/**
 * Webtech HR Engine™ - SuperAdmin Platform Settings Dashboard
 * Phase 7: Paramètres de la plateforme
 */

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';

// UI Components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Skeleton } from './ui/skeleton';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from './ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

// Icons
import {
  Settings,
  DollarSign,
  CreditCard,
  Mail,
  Server,
  Save,
  RefreshCw,
  History,
  AlertTriangle,
  CheckCircle2,
  CheckCircle,
  Shield,
  Loader2,
  Percent,
  Users,
  Calendar,
  FileText,
  Bell,
  Wrench,
  Eye,
  EyeOff,
  TestTube,
  Clock,
  ChevronRight,
  Edit,
  Phone,
  MapPin,
  Globe,
  Key,
  Send,
  AlertCircle,
} from 'lucide-react';

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-FR').format(amount) + ' F CFA';
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'Jamais';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Settings Section Component
function SettingsSection({ title, description, icon: Icon, children, onSave, saving, hasChanges }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Icon className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
          </div>
          {onSave && (
            <Button 
              onClick={onSave} 
              disabled={saving || !hasChanges}
              className="bg-webtech-blue hover:bg-webtech-blue-hover"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {children}
      </CardContent>
    </Card>
  );
}

// Pricing Tiers Settings Tab
function PricingSettings({ settings, onUpdate }) {
  const [tiers, setTiers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingTier, setEditingTier] = useState(null);

  // Default tier for new entries
  const defaultTier = {
    id: '',
    name: '',
    minUsers: 0,
    maxUsers: 100,
    priceMonthly: 0,
    priceYearly: 0,
    maxEmployees: 500,
    features: [],
    contactRequired: false
  };

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/superadmin/settings/pricing-tiers');
      setTiers(response.data.tiers || []);
    } catch (error) {
      console.error('Error fetching tiers:', error);
      toast.error('Erreur lors du chargement des tranches');
    } finally {
      setLoading(false);
    }
  };

  const handleTierChange = (index, field, value) => {
    setTiers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setHasChanges(true);
  };

  const handleAddTier = () => {
    const newTier = {
      ...defaultTier,
      id: `tier_${Date.now()}`,
      name: `Tranche ${tiers.length + 1}`,
      minUsers: tiers.length > 0 ? (tiers[tiers.length - 1].maxUsers || 0) + 1 : 10
    };
    setTiers(prev => [...prev, newTier]);
    setHasChanges(true);
    setEditingTier(tiers.length);
  };

  const handleRemoveTier = (index) => {
    if (tiers.length <= 1) {
      toast.error('Vous devez garder au moins une tranche');
      return;
    }
    setTiers(prev => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Validation
    for (const tier of tiers) {
      if (!tier.name || !tier.id) {
        toast.error('Chaque tranche doit avoir un nom et un ID');
        return;
      }
      if (!tier.contactRequired && (!tier.priceMonthly || !tier.priceYearly)) {
        toast.error(`La tranche "${tier.name}" doit avoir des prix définis`);
        return;
      }
    }

    setSaving(true);
    try {
      await api.put('/superadmin/settings/pricing-tiers', { tiers });
      toast.success('Tranches de prix mises à jour');
      setHasChanges(false);
      onUpdate();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <SettingsSection
      title="Tranches de Prix PRO"
      description="Configurez les tarifs par tranche d'utilisateurs"
      icon={DollarSign}
      onSave={handleSave}
      saving={saving}
      hasChanges={hasChanges}
    >
      <div className="space-y-6">
        {/* Tiers List */}
        <div className="space-y-4">
          {tiers.map((tier, index) => (
            <div 
              key={tier.id || index}
              className={`p-4 border rounded-lg ${tier.contactRequired ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Badge 
                    className={tier.contactRequired 
                      ? 'bg-amber-100 text-amber-700' 
                      : 'bg-blue-100 text-blue-700'
                    }
                  >
                    {tier.name || `Tranche ${index + 1}`}
                  </Badge>
                  <span className="text-sm text-slate-500">
                    {tier.minUsers} - {tier.maxUsers || '∞'} utilisateurs
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingTier(editingTier === index ? null : index)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  {!tier.contactRequired && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleRemoveTier(index)}
                    >
                      ×
                    </Button>
                  )}
                </div>
              </div>

              {/* Price Display */}
              {!tier.contactRequired ? (
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Mensuel:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(tier.priceMonthly)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Annuel:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(tier.priceYearly)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Max employés:</span>
                    <span className="ml-2 font-semibold">{tier.maxEmployees || '∞'}</span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-amber-700">
                  <Phone className="w-4 h-4" />
                  <span className="font-medium">Contactez-nous pour un devis personnalisé</span>
                </div>
              )}

              {/* Edit Form */}
              {editingTier === index && (
                <div className="mt-4 pt-4 border-t border-slate-200 space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label>ID (unique)</Label>
                      <Input
                        value={tier.id}
                        onChange={(e) => handleTierChange(index, 'id', e.target.value.toLowerCase().replace(/\s/g, '_'))}
                        placeholder="starter"
                      />
                    </div>
                    <div>
                      <Label>Nom</Label>
                      <Input
                        value={tier.name}
                        onChange={(e) => handleTierChange(index, 'name', e.target.value)}
                        placeholder="Starter"
                      />
                    </div>
                    <div>
                      <Label>Min utilisateurs</Label>
                      <Input
                        type="number"
                        value={tier.minUsers}
                        onChange={(e) => handleTierChange(index, 'minUsers', parseInt(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label>Max utilisateurs</Label>
                      <Input
                        type="number"
                        value={tier.maxUsers || ''}
                        onChange={(e) => handleTierChange(index, 'maxUsers', e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="∞"
                        disabled={tier.contactRequired}
                      />
                    </div>
                  </div>

                  {!tier.contactRequired && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Prix mensuel (F CFA)</Label>
                        <Input
                          type="number"
                          value={tier.priceMonthly || ''}
                          onChange={(e) => handleTierChange(index, 'priceMonthly', parseInt(e.target.value) || 0)}
                          placeholder="250000"
                        />
                      </div>
                      <div>
                        <Label>Prix annuel (F CFA)</Label>
                        <Input
                          type="number"
                          value={tier.priceYearly || ''}
                          onChange={(e) => handleTierChange(index, 'priceYearly', parseInt(e.target.value) || 0)}
                          placeholder="2400000"
                        />
                      </div>
                      <div>
                        <Label>Max employés</Label>
                        <Input
                          type="number"
                          value={tier.maxEmployees || ''}
                          onChange={(e) => handleTierChange(index, 'maxEmployees', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="500"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={tier.contactRequired}
                      onCheckedChange={(checked) => handleTierChange(index, 'contactRequired', checked)}
                    />
                    <Label>Devis sur demande (Entreprise)</Label>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Tier Button */}
        <Button
          variant="outline"
          onClick={handleAddTier}
          className="w-full"
        >
          + Ajouter une tranche
        </Button>

        {/* Preview */}
        <div className="p-4 bg-slate-50 rounded-lg">
          <h4 className="text-sm font-medium text-slate-700 mb-3">Aperçu des tranches</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Tranche</th>
                  <th className="text-left py-2 px-3">Utilisateurs</th>
                  <th className="text-right py-2 px-3">Mensuel</th>
                  <th className="text-right py-2 px-3">Annuel</th>
                  <th className="text-right py-2 px-3">Max Employés</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier, index) => (
                  <tr key={index} className="border-b">
                    <td className="py-2 px-3 font-medium">{tier.name}</td>
                    <td className="py-2 px-3">{tier.minUsers} - {tier.maxUsers || '∞'}</td>
                    <td className="py-2 px-3 text-right">
                      {tier.contactRequired ? 'Sur devis' : formatCurrency(tier.priceMonthly)}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {tier.contactRequired ? 'Sur devis' : formatCurrency(tier.priceYearly)}
                    </td>
                    <td className="py-2 px-3 text-right">{tier.maxEmployees || '∞'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SettingsSection>
  );
}

// PayTech Settings Tab
function PaytechSettings({ settings, onUpdate }) {
  const [formData, setFormData] = useState({
    paytechMode: 'sandbox',
    paytechApiKey: '',
    paytechApiSecret: ''
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Vérifier si PayTech est configuré
  const isConfigured = settings?.paytechApiKeyConfigured && settings?.paytechMerchantIdConfigured;

  useEffect(() => {
    if (settings) {
      setFormData(prev => ({
        ...prev,
        paytechMode: settings.paytechMode || 'sandbox'
      }));
    }
  }, [settings]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSend = { 
        paytechMode: formData.paytechMode 
      };
      // Only send keys if they were modified
      if (formData.paytechApiKey) {
        dataToSend.paytechApiKey = formData.paytechApiKey;
      }
      if (formData.paytechApiSecret) {
        dataToSend.paytechMerchantId = formData.paytechApiSecret; // API_SECRET
      }
      
      await api.put('/superadmin/settings/paytech', dataToSend);
      toast.success('Configuration PayTech mise à jour');
      setHasChanges(false);
      setFormData(prev => ({ ...prev, paytechApiKey: '', paytechApiSecret: '' }));
      onUpdate();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await api.post('/superadmin/settings/test-paytech');
      if (response.data.success) {
        toast.success(response.data.message || 'Connexion réussie');
      } else {
        toast.error(response.data.message || 'Échec de la connexion');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du test');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <CreditCard className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Passerelle de paiement PayTech</CardTitle>
              <CardDescription>
                Connectez votre compte PayTech pour recevoir des paiements
              </CardDescription>
            </div>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving || !hasChanges}
            className="bg-webtech-blue hover:bg-webtech-blue-hover"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status Card */}
        <div className={`p-4 rounded-lg border-2 ${
          isConfigured 
            ? 'bg-green-50 border-green-200' 
            : 'bg-amber-50 border-amber-200'
        }`}>
          <div className="flex items-center gap-3">
            {isConfigured ? (
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            )}
            <div>
              <p className={`font-semibold ${isConfigured ? 'text-green-800' : 'text-amber-800'}`}>
                {isConfigured ? 'PayTech est configuré' : 'Configuration requise'}
              </p>
              <p className={`text-sm ${isConfigured ? 'text-green-700' : 'text-amber-700'}`}>
                {isConfigured 
                  ? `Mode: ${formData.paytechMode === 'production' ? 'Production (paiements réels)' : 'Sandbox (tests)'}`
                  : 'Ajoutez vos clés API pour activer les paiements'}
              </p>
            </div>
          </div>
        </div>

        {/* Instructions */}
        {!isConfigured && (
          <div className="bg-slate-50 rounded-lg p-4 text-sm">
            <p className="font-medium text-slate-800 mb-2">Comment obtenir vos clés API ?</p>
            <ol className="list-decimal list-inside space-y-1 text-slate-600">
              <li>Connectez-vous sur <a href="https://paytech.sn" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">paytech.sn</a></li>
              <li>Allez dans <strong>Paramètres → API</strong></li>
              <li>Copiez votre <strong>API_KEY</strong> et <strong>API_SECRET</strong></li>
            </ol>
          </div>
        )}

        {/* Mode Selection */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Environnement</Label>
          <Select value={formData.paytechMode} onValueChange={(v) => handleChange('paytechMode', v)}>
            <SelectTrigger className="w-full md:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sandbox">
                <div className="flex items-center gap-2">
                  <TestTube className="w-4 h-4 text-amber-500" />
                  <span>Sandbox (Test)</span>
                </div>
              </SelectItem>
              <SelectItem value="production">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>Production</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500">
            {formData.paytechMode === 'sandbox' 
              ? 'Utilisez le mode Sandbox pour tester sans vrais paiements.' 
              : '⚠️ Les paiements seront réels en mode Production.'}
          </p>
        </div>

        <Separator />

        {/* API Keys */}
        <div className="space-y-4">
          <h4 className="font-medium text-slate-900">Clés API PayTech</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* API_KEY */}
            <div className="space-y-2">
              <Label htmlFor="apiKey" className="flex items-center gap-2">
                API_KEY
                {settings?.paytechApiKeyConfigured && (
                  <Badge className="bg-green-100 text-green-700 text-xs">Configurée</Badge>
                )}
              </Label>
              <div className="relative">
                <Input
                  id="apiKey"
                  type={showApiKey ? 'text' : 'password'}
                  value={formData.paytechApiKey}
                  onChange={(e) => handleChange('paytechApiKey', e.target.value)}
                  placeholder={settings?.paytechApiKeyConfigured ? 'Laisser vide pour conserver' : 'Collez votre API_KEY'}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* API_SECRET */}
            <div className="space-y-2">
              <Label htmlFor="apiSecret" className="flex items-center gap-2">
                API_SECRET
                {settings?.paytechMerchantIdConfigured && (
                  <Badge className="bg-green-100 text-green-700 text-xs">Configuré</Badge>
                )}
              </Label>
              <div className="relative">
                <Input
                  id="apiSecret"
                  type={showApiSecret ? 'text' : 'password'}
                  value={formData.paytechApiSecret}
                  onChange={(e) => handleChange('paytechApiSecret', e.target.value)}
                  placeholder={settings?.paytechMerchantIdConfigured ? 'Laisser vide pour conserver' : 'Collez votre API_SECRET'}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                  onClick={() => setShowApiSecret(!showApiSecret)}
                >
                  {showApiSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Test Connection - only show if configured */}
        {isConfigured && (
          <>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">Tester la connexion</p>
                <p className="text-sm text-slate-500">Vérifiez que vos clés API fonctionnent</p>
              </div>
              <Button variant="outline" onClick={handleTest} disabled={testing}>
                {testing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <TestTube className="w-4 h-4 mr-2" />
                )}
                Tester
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Billing Settings Tab
function BillingSettings({ settings, onUpdate }) {
  const [formData, setFormData] = useState({
    invoicePrefix: 'INV',
    gracePeriodDays: 7,
    taxRate: 0
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (settings) {
      setFormData({
        invoicePrefix: settings.invoicePrefix || 'INV',
        gracePeriodDays: settings.gracePeriodDays || 7,
        taxRate: settings.taxRate || 0
      });
    }
  }, [settings]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/superadmin/settings/billing', formData);
      toast.success('Paramètres de facturation mis à jour');
      setHasChanges(false);
      onUpdate();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection
      title="Facturation"
      description="Paramètres des factures et périodes de grâce"
      icon={FileText}
      onSave={handleSave}
      saving={saving}
      hasChanges={hasChanges}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-2">
          <Label htmlFor="invoicePrefix">Préfixe des factures</Label>
          <Input
            id="invoicePrefix"
            value={formData.invoicePrefix}
            onChange={(e) => handleChange('invoicePrefix', e.target.value.toUpperCase())}
            maxLength={5}
          />
          <p className="text-xs text-slate-500">Ex: {formData.invoicePrefix}-2024-00001</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="gracePeriodDays">Période de grâce (jours)</Label>
          <Input
            id="gracePeriodDays"
            type="number"
            value={formData.gracePeriodDays}
            onChange={(e) => handleChange('gracePeriodDays', parseInt(e.target.value) || 0)}
            min={0}
            max={30}
          />
          <p className="text-xs text-slate-500">Délai avant suspension</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="taxRate">TVA (%)</Label>
          <div className="relative">
            <Input
              id="taxRate"
              type="number"
              value={formData.taxRate}
              onChange={(e) => handleChange('taxRate', parseFloat(e.target.value) || 0)}
              min={0}
              max={100}
              step={0.1}
              className="pr-8"
            />
            <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          </div>
        </div>
      </div>
    </SettingsSection>
  );
}

// Email Settings Tab
function EmailSettings({ settings, onUpdate }) {
  const [formData, setFormData] = useState({
    sendPaymentReminders: true,
    reminderDaysBefore: 3,
    sendWelcomeEmail: true,
    sendInvoiceEmail: true
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // Provider config state (Microsoft 365 only — SendGrid retired for system stability)
  const [providerConfig, setProviderConfig] = useState({
    provider: 'microsoft365',
    sendgridApiKey: '',
    microsoft365TenantId: '',
    microsoft365ClientId: '',
    microsoft365ClientSecret: '',
    senderEmail: '',
    senderName: 'HR Engine',
    isConfigured: false
  });
  const [savingProvider, setSavingProvider] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');

  useEffect(() => {
    if (settings) {
      setFormData({
        sendPaymentReminders: settings.sendPaymentReminders ?? true,
        reminderDaysBefore: settings.reminderDaysBefore || 3,
        sendWelcomeEmail: settings.sendWelcomeEmail ?? true,
        sendInvoiceEmail: settings.sendInvoiceEmail ?? true
      });
    }
    fetchTemplates();
    fetchProviderConfig();
  }, [settings]);

  const fetchProviderConfig = async () => {
    try {
      const response = await api.get('/admin/emails/config/provider');
      if (response.data) {
        setProviderConfig(prev => ({
          ...prev,
          ...response.data,
          provider: 'microsoft365',  // SendGrid retired — always force M365
          sendgridApiKey: response.data.sendgridApiKey || '',
          microsoft365ClientSecret: response.data.microsoft365ClientSecret || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching email provider config:', error);
    }
  };

  const saveProviderConfig = async () => {
    setSavingProvider(true);
    try {
      await api.put('/admin/emails/config/provider', providerConfig);
      toast.success('Configuration du fournisseur email enregistrée');
      fetchProviderConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingProvider(false);
    }
  };

  const testEmailSend = async () => {
    if (!testEmailAddress) {
      toast.error('Veuillez entrer une adresse email de test');
      return;
    }
    setTestingEmail(true);
    try {
      await api.post('/admin/emails/test', { email: testEmailAddress });
      toast.success(`Email de test envoyé à ${testEmailAddress}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du test');
    } finally {
      setTestingEmail(false);
    }
  };

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await api.get('/superadmin/email-templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/superadmin/settings/email', formData);
      toast.success('Paramètres email mis à jour');
      setHasChanges(false);
      onUpdate();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({
      ...template,
      subject: template.subject,
      content: template.content
    });
    setPreviewData(null);
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    setSavingTemplate(true);
    try {
      await api.put(`/superadmin/email-templates/${editingTemplate.id}`, {
        subject: editingTemplate.subject,
        content: editingTemplate.content
      });
      toast.success('Template mis à jour');
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetTemplate = async (templateId) => {
    try {
      await api.post(`/superadmin/email-templates/${templateId}/reset`);
      toast.success('Template réinitialisé');
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const handlePreview = async () => {
    if (!editingTemplate) return;
    try {
      const response = await api.post(`/superadmin/email-templates/${editingTemplate.id}/preview`);
      setPreviewData(response.data);
    } catch (error) {
      toast.error('Erreur lors de la prévisualisation');
    }
  };

  return (
    <div className="space-y-6">
      {/* Email Provider Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Settings className="w-5 h-5 text-blue-500" />
            Configuration du fournisseur d&apos;emails
          </CardTitle>
          <CardDescription>
            Configuration du service d&apos;envoi d&apos;emails — Microsoft 365 Graph API
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Provider info — Microsoft 365 only */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Fournisseur d&apos;emails</Label>
            <div className="p-4 border-2 border-blue-500 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0078d4] rounded-lg flex items-center justify-center text-white font-bold text-xs">
                  M365
                </div>
                <div className="flex-1">
                  <p className="font-medium">Microsoft 365 Graph API</p>
                  <p className="text-sm text-slate-500">Service unique recommandé pour la fiabilité des notifications système.</p>
                </div>
              </div>
            </div>
          </div>

          {/* SendGrid Config — retired */}
          {false && providerConfig.provider === 'sendgrid' && (
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium flex items-center gap-2">
                <Key className="w-4 h-4" />
                Configuration SendGrid
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sendgrid-key">Clé API SendGrid</Label>
                  <Input
                    id="sendgrid-key"
                    type="password"
                    placeholder="SG.xxxxx..."
                    value={providerConfig.sendgridApiKey}
                    onChange={(e) => setProviderConfig({ ...providerConfig, sendgridApiKey: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sender-email-sg">Email expéditeur</Label>
                  <Input
                    id="sender-email-sg"
                    type="email"
                    placeholder="noreply@votreentreprise.com"
                    value={providerConfig.senderEmail}
                    onChange={(e) => setProviderConfig({ ...providerConfig, senderEmail: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Microsoft 365 Config */}
          {providerConfig.provider === 'microsoft365' && (
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium flex items-center gap-2">
                <Key className="w-4 h-4" />
                Configuration Microsoft 365 Graph API
              </h4>
              <p className="text-sm text-slate-600">
                Pour configurer Microsoft Graph API, vous devez créer une application dans Azure AD.
                <a 
                  href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline ml-1"
                >
                  Accéder au portail Azure →
                </a>
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="m365-tenant">Tenant ID (Directory ID)</Label>
                  <Input
                    id="m365-tenant"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={providerConfig.microsoft365TenantId}
                    onChange={(e) => setProviderConfig({ ...providerConfig, microsoft365TenantId: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="m365-client">Client ID (Application ID)</Label>
                  <Input
                    id="m365-client"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={providerConfig.microsoft365ClientId}
                    onChange={(e) => setProviderConfig({ ...providerConfig, microsoft365ClientId: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="m365-secret">Client Secret</Label>
                  <Input
                    id="m365-secret"
                    type="password"
                    placeholder="Votre secret client..."
                    value={providerConfig.microsoft365ClientSecret}
                    onChange={(e) => setProviderConfig({ ...providerConfig, microsoft365ClientSecret: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="m365-sender">Email expéditeur (Microsoft 365)</Label>
                  <Input
                    id="m365-sender"
                    type="email"
                    placeholder="notifications@votreentreprise.com"
                    value={providerConfig.senderEmail}
                    onChange={(e) => setProviderConfig({ ...providerConfig, senderEmail: e.target.value })}
                  />
                </div>
              </div>
              <Alert className="bg-amber-50 border-amber-200">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <AlertTitle className="text-amber-800">Permissions requises</AlertTitle>
                <AlertDescription className="text-amber-700">
                  L&apos;application Azure AD doit avoir la permission <code className="bg-amber-100 px-1 rounded">Mail.Send</code> (Application permission, pas Delegated).
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Common Config */}
          <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
            <h4 className="font-medium">Paramètres généraux</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sender-name">Nom de l&apos;expéditeur</Label>
                <Input
                  id="sender-name"
                  placeholder="HR Engine"
                  value={providerConfig.senderName}
                  onChange={(e) => setProviderConfig({ ...providerConfig, senderName: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Save and Test */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              {providerConfig.isConfigured ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-green-600">Configuration active</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <span className="text-amber-600">Non configuré</span>
                </>
              )}
            </div>
            <Button 
              onClick={saveProviderConfig} 
              disabled={savingProvider}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {savingProvider ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer la configuration
            </Button>
          </div>

          {/* Test Email */}
          <div className="pt-4 border-t">
            <h4 className="font-medium mb-3">Tester la configuration</h4>
            <div className="flex gap-3">
              <Input
                type="email"
                placeholder="votre@email.com"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                className="max-w-xs"
              />
              <Button 
                variant="outline" 
                onClick={testEmailSend}
                disabled={testingEmail || !testEmailAddress}
              >
                {testingEmail ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Envoyer un test
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Settings Section */}
      <SettingsSection
        title="Notifications Email"
        description="Activez ou désactivez les emails automatiques"
        icon={Mail}
        onSave={handleSave}
        saving={saving}
        hasChanges={hasChanges}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">Rappels de paiement</p>
                <p className="text-sm text-slate-500">Rappels avant échéance</p>
              </div>
            </div>
            <Switch
              checked={formData.sendPaymentReminders}
              onCheckedChange={(v) => handleChange('sendPaymentReminders', v)}
            />
          </div>

          {formData.sendPaymentReminders && (
            <div className="ml-12 flex items-center gap-2">
              <Label className="text-sm">Envoyer</Label>
              <Input
                type="number"
                value={formData.reminderDaysBefore}
                onChange={(e) => handleChange('reminderDaysBefore', parseInt(e.target.value) || 1)}
                min={1}
                max={14}
                className="w-16 h-8"
              />
              <span className="text-sm text-slate-500">jours avant</span>
            </div>
          )}

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">Email de bienvenue</p>
                <p className="text-sm text-slate-500">Nouveaux utilisateurs</p>
              </div>
            </div>
            <Switch
              checked={formData.sendWelcomeEmail}
              onCheckedChange={(v) => handleChange('sendWelcomeEmail', v)}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">Factures par email</p>
                <p className="text-sm text-slate-500">Envoi automatique</p>
              </div>
            </div>
            <Switch
              checked={formData.sendInvoiceEmail}
              onCheckedChange={(v) => handleChange('sendInvoiceEmail', v)}
            />
          </div>
        </div>
      </SettingsSection>

      {/* Templates Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Edit className="w-5 h-5" />
            Templates d&apos;emails
          </CardTitle>
          <CardDescription>
            Personnalisez le contenu des emails envoyés par la plateforme
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingTemplates ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-2">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50"
                >
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="font-medium text-slate-900">{template.name}</p>
                      <p className="text-xs text-slate-500">{template.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {template.isCustomized && (
                      <Badge className="bg-blue-100 text-blue-700 text-xs">Personnalisé</Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditTemplate(template)}
                      className="text-slate-600 hover:text-slate-900"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Modifier
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Template Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le template : {editingTemplate?.name}</DialogTitle>
            <DialogDescription>
              {editingTemplate?.description}
            </DialogDescription>
          </DialogHeader>
          
          {editingTemplate && (
            <div className="space-y-4">
              {/* Variables disponibles */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm font-medium text-blue-800 mb-2">Variables disponibles :</p>
                <div className="flex flex-wrap gap-2">
                  {editingTemplate.variables?.map((v) => (
                    <code key={v} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                      {`{{${v}}}`}
                    </code>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label>Sujet de l&apos;email</Label>
                <Input
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                  placeholder="Sujet..."
                />
              </div>

              {/* Content */}
              <div className="space-y-2">
                <Label>Contenu de l&apos;email</Label>
                <Textarea
                  value={editingTemplate.content}
                  onChange={(e) => setEditingTemplate({...editingTemplate, content: e.target.value})}
                  placeholder="Contenu..."
                  rows={10}
                  className="font-mono text-sm"
                />
              </div>

              {/* Preview */}
              {previewData && (
                <div className="space-y-2">
                  <Label>Aperçu</Label>
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <p className="font-medium text-slate-900 mb-2">Sujet : {previewData.subject}</p>
                    <Separator className="my-2" />
                    <pre className="whitespace-pre-wrap text-sm text-slate-700">{previewData.content}</pre>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => handleResetTemplate(editingTemplate.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Réinitialiser
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handlePreview}>
                    <Eye className="w-4 h-4 mr-2" />
                    Aperçu
                  </Button>
                  <Button onClick={handleSaveTemplate} disabled={savingTemplate}>
                    {savingTemplate && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// System Settings Tab
function SystemSettings({ settings, onUpdate }) {
  const [formData, setFormData] = useState({
    maintenanceMode: false,
    maintenanceMessage: '',
    maxEmployeesFreePlan: 10,
    maxUsersFreePlan: 3
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (settings) {
      setFormData({
        maintenanceMode: settings.maintenanceMode ?? false,
        maintenanceMessage: settings.maintenanceMessage || '',
        maxEmployeesFreePlan: settings.maxEmployeesFreePlan || 10,
        maxUsersFreePlan: settings.maxUsersFreePlan || 3
      });
    }
  }, [settings]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/superadmin/settings/system', formData);
      toast.success('Paramètres système mis à jour');
      setHasChanges(false);
      onUpdate();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection
      title="Paramètres Système"
      description="Configuration globale de la plateforme"
      icon={Server}
      onSave={handleSave}
      saving={saving}
      hasChanges={hasChanges}
    >
      {/* Maintenance Mode Alert */}
      {formData.maintenanceMode && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <AlertTitle className="text-red-800">Mode maintenance actif</AlertTitle>
          <AlertDescription className="text-red-700">
            Les utilisateurs ne peuvent pas accéder à la plateforme.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-3">
            <Wrench className="w-5 h-5 text-slate-500" />
            <div>
              <p className="font-medium text-slate-900">Mode maintenance</p>
              <p className="text-sm text-slate-500">Désactive temporairement la plateforme</p>
            </div>
          </div>
          <Switch
            checked={formData.maintenanceMode}
            onCheckedChange={(v) => handleChange('maintenanceMode', v)}
          />
        </div>

        {formData.maintenanceMode && (
          <div className="space-y-2">
            <Label>Message de maintenance</Label>
            <Textarea
              value={formData.maintenanceMessage}
              onChange={(e) => handleChange('maintenanceMessage', e.target.value)}
              placeholder="Maintenance en cours. Veuillez réessayer plus tard..."
              rows={2}
            />
          </div>
        )}

        <Separator />

        <h4 className="font-medium text-slate-900">Limites Plan Gratuit</h4>
        <p className="text-sm text-slate-500 mb-4">Ces limites s&apos;appliquent aux entreprises avec le plan GRATUIT</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="maxEmployees">Maximum d&apos;employés</Label>
            <Input
              id="maxEmployees"
              type="number"
              value={formData.maxEmployeesFreePlan}
              onChange={(e) => handleChange('maxEmployeesFreePlan', parseInt(e.target.value) || 1)}
              min={1}
            />
            <p className="text-xs text-slate-500">Nombre max d&apos;employés par entreprise gratuite</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="maxUsers">Maximum d&apos;utilisateurs</Label>
            <Input
              id="maxUsers"
              type="number"
              value={formData.maxUsersFreePlan}
              onChange={(e) => handleChange('maxUsersFreePlan', parseInt(e.target.value) || 1)}
              min={1}
            />
            <p className="text-xs text-slate-500">Nombre max de comptes utilisateurs (Admin, Manager...)</p>
          </div>
        </div>
      </div>
    </SettingsSection>
  );
}

// History Tab
function SettingsHistory({ history, loading }) {
  const getActionIcon = (action) => {
    if (action.includes('pricing')) return DollarSign;
    if (action.includes('paytech')) return CreditCard;
    if (action.includes('billing')) return FileText;
    if (action.includes('email')) return Mail;
    if (action.includes('system')) return Server;
    return Settings;
  };

  const getActionLabel = (action) => {
    const labels = {
      'pricing_settings_updated': 'Tarification modifiée',
      'paytech_settings_updated': 'PayTech modifié',
      'billing_settings_updated': 'Facturation modifiée',
      'email_settings_updated': 'Emails modifiés',
      'system_settings_updated': 'Système modifié'
    };
    return labels[action] || action;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (!history?.length) {
    return (
      <div className="text-center py-12 text-slate-500">
        <History className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <p>Aucun historique de modifications</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {history.map((item, idx) => {
        const Icon = getActionIcon(item.action);
        return (
          <div key={idx} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Icon className="w-4 h-4 text-slate-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-900">{getActionLabel(item.action)}</p>
              <p className="text-sm text-slate-500">
                par {item.performedByEmail}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600">{formatDate(item.createdAt)}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </div>
        );
      })}
    </div>
  );
}

// Contact Settings Component
function ContactSettings({ settings, onUpdate }) {
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    address: '',
    whatsapp: '',
    linkedin: '',
    facebook: ''
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const fetchContactSettings = async () => {
      try {
        const response = await api.get('/superadmin/settings/contact');
        setFormData({
          email: response.data.email || '',
          phone: response.data.phone || '',
          address: response.data.address || '',
          whatsapp: response.data.whatsapp || '',
          linkedin: response.data.linkedin || '',
          facebook: response.data.facebook || ''
        });
      } catch (error) {
        console.error('Error fetching contact settings:', error);
      }
    };
    fetchContactSettings();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/superadmin/settings/contact', formData);
      toast.success('Informations de contact mises à jour');
      setHasChanges(false);
      onUpdate?.();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection
      title="Informations de Contact"
      description="Configurez les coordonnées affichées sur le site public"
      icon={Phone}
      onSave={handleSave}
      saving={saving}
      hasChanges={hasChanges}
    >
      <div className="space-y-6">
        {/* Email et Téléphone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-500" />
              Email de contact
            </Label>
            <Input
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="contact@example.com"
              type="email"
            />
            <p className="text-xs text-slate-500">Email affiché sur le site et dans le footer</p>
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-500" />
              Téléphone
            </Label>
            <Input
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+223 XX XX XX XX"
            />
            <p className="text-xs text-slate-500">Numéro de téléphone principal</p>
          </div>
        </div>

        {/* Adresse */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-500" />
            Adresse
          </Label>
          <Input
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            placeholder="Bamako, Mali"
          />
          <p className="text-xs text-slate-500">Adresse physique de l'entreprise</p>
        </div>

        <Separator />

        <h4 className="font-medium flex items-center gap-2">
          <Globe className="w-4 h-4" />
          Réseaux sociaux
        </h4>

        {/* Réseaux sociaux */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>WhatsApp</Label>
            <Input
              value={formData.whatsapp}
              onChange={(e) => handleChange('whatsapp', e.target.value)}
              placeholder="+223 XX XX XX XX"
            />
          </div>
          <div className="space-y-2">
            <Label>LinkedIn</Label>
            <Input
              value={formData.linkedin}
              onChange={(e) => handleChange('linkedin', e.target.value)}
              placeholder="https://linkedin.com/company/..."
            />
          </div>
          <div className="space-y-2">
            <Label>Facebook</Label>
            <Input
              value={formData.facebook}
              onChange={(e) => handleChange('facebook', e.target.value)}
              placeholder="https://facebook.com/..."
            />
          </div>
        </div>

        {/* Preview */}
        <div className="bg-slate-50 rounded-lg p-4">
          <h5 className="text-sm font-medium text-slate-700 mb-2">Aperçu (Footer)</h5>
          <div className="text-sm text-slate-600 space-y-1">
            <p>📧 {formData.email || 'contact@example.com'}</p>
            <p>📞 {formData.phone || '+223 XX XX XX XX'}</p>
            <p>📍 {formData.address || 'Bamako, Mali'}</p>
          </div>
        </div>
      </div>
    </SettingsSection>
  );
}

// Main Component
export default function SuperAdminSettingsDashboard() {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('pricing');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, historyRes] = await Promise.all([
        api.get('/superadmin/settings'),
        api.get('/superadmin/settings/history')
      ]);
      setSettings(settingsRes.data);
      setHistory(historyRes.data.history || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Settings className="w-6 h-6 text-webtech-blue" />
            Paramètres de la Plateforme
          </h2>
          <p className="text-slate-600 mt-1">
            Configurez les paramètres globaux de HR Engine
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {settings?.lastUpdatedAt && (
            <span className="text-sm text-slate-500 flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Dernière modification: {formatDate(settings.lastUpdatedAt)}
            </span>
          )}
          <Button variant="outline" onClick={fetchSettings} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="pricing" className="data-[state=active]:bg-white">
            <DollarSign className="w-4 h-4 mr-2" />
            Tarification
          </TabsTrigger>
          <TabsTrigger value="paytech" className="data-[state=active]:bg-white">
            <CreditCard className="w-4 h-4 mr-2" />
            PayTech
          </TabsTrigger>
          <TabsTrigger value="billing" className="data-[state=active]:bg-white">
            <FileText className="w-4 h-4 mr-2" />
            Facturation
          </TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-white">
            <Mail className="w-4 h-4 mr-2" />
            Emails
          </TabsTrigger>
          <TabsTrigger value="system" className="data-[state=active]:bg-white">
            <Server className="w-4 h-4 mr-2" />
            Système
          </TabsTrigger>
          <TabsTrigger value="contact" className="data-[state=active]:bg-white">
            <Phone className="w-4 h-4 mr-2" />
            Contact
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white">
            <History className="w-4 h-4 mr-2" />
            Historique
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <>
            <TabsContent value="pricing">
              <PricingSettings settings={settings?.pricing} onUpdate={fetchSettings} />
            </TabsContent>

            <TabsContent value="paytech">
              <PaytechSettings settings={settings?.paytech} onUpdate={fetchSettings} />
            </TabsContent>

            <TabsContent value="billing">
              <BillingSettings settings={settings?.billing} onUpdate={fetchSettings} />
            </TabsContent>

            <TabsContent value="email">
              <EmailSettings settings={settings?.email} onUpdate={fetchSettings} />
            </TabsContent>

            <TabsContent value="system">
              <SystemSettings settings={settings?.system} onUpdate={fetchSettings} />
            </TabsContent>

            <TabsContent value="contact">
              <ContactSettings settings={settings?.contact} onUpdate={fetchSettings} />
            </TabsContent>

            <TabsContent value="history">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-slate-500" />
                    Historique des modifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SettingsHistory history={history} loading={loading} />
                </CardContent>
              </Card>
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
